import type { BaseMetadata, ThreadData } from "@liveblocks/client"
import type {
  EmbedBlock,
  ImageBlock,
  TextBlock,
} from "@prismicio/types-internal/lib/content"
import type { Node } from "@tiptap/core"
import type { Node as ProsemirrorNode, Schema } from "@tiptap/pm/model"

import type { BoldExtension } from "../extensions/Bold/BoldModel"
import type { Level } from "../extensions/Heading/Heading"
import type { ItalicExtension } from "../extensions/Italic/ItalicModel"
import type { LabelExtension } from "../extensions/Label/LabelModel"
import type { LinkExtension } from "../extensions/Link/LinkModel"
import type {
  ExtendedRichTextContent,
  ExtendedRichTextNodeType,
  TableBlock,
} from "../types"

export enum ExtensionType {
  Document = "document",
  Mark = "mark",
  Node = "node",
}

export enum NodeType {
  Text = "text",
  Embed = "embed",
  Image = "image",
  Table = "table",
}

export type Extension = WidgetExtension | DocumentExtension

export type WidgetExtension = MarkExtension | NodeExtension

export type ExtensionPlaceholder =
  | string
  | ((nodeAttrs: ProsemirrorNode["attrs"]) => string | undefined)
  | undefined

export type DocumentConverterReturnType<T extends ExtendedRichTextContent> = {
  fromPrismic(
    id: string,
    schema: Schema,
    nodeTypes: Set<ExtendedRichTextNodeType>,
    content: T,
    threads?: ThreadData<BaseMetadata>[],
  ): ProsemirrorNode | undefined

  toPrismic(node: ProsemirrorNode): T | undefined
}
export class DocumentExtension {
  extType: ExtensionType.Document = ExtensionType.Document

  constructor(
    readonly name: string,
    readonly component: (multiLines: boolean) => Node,
    readonly converter: <
      T extends ExtendedRichTextContent = ExtendedRichTextContent,
    >(
      extensions: NodeExtension[],
    ) => DocumentConverterReturnType<T>,
  ) {}
}

export type MarkExtension =
  | LinkExtension
  | BoldExtension
  | ItalicExtension
  | LabelExtension

export type NodeExtension =
  | TextExtension
  | EmbedExtension
  | ImageExtension
  | TableExtension

// Interface for a Prismic Text Block
export class TextExtension {
  extType: ExtensionType.Node = ExtensionType.Node
  nodeType: NodeType.Text = NodeType.Text

  constructor(
    readonly richTextNodeTypes: ExtendedRichTextNodeType[],
    readonly name: string,
    readonly component: Node,
    readonly converter: {
      fromPrismic(
        id: string,
        schema: Schema,
        threads?: ThreadData<BaseMetadata>[],
      ): (elmt: TextBlock) => ProsemirrorNode | undefined
      toPrismic(
        elmt: ProsemirrorNode,
      ): TextBlock | readonly TextBlock[] | undefined
    },
    readonly placeholder?: ExtensionPlaceholder,
    readonly lockedFormat = false,
  ) {}
}

export function isTextExtension(
  extension: WidgetExtension,
): extension is TextExtension {
  return extension instanceof TextExtension
}

// Interface for an Embed Text Block
export class EmbedExtension {
  extType = ExtensionType.Node
  nodeType: NodeType.Embed = NodeType.Embed

  constructor(
    readonly richTextNodeTypes: ExtendedRichTextNodeType[],
    readonly name: string,
    readonly component: Node,
    readonly converter: {
      fromPrismic(
        schema: Schema,
      ): (elmt: EmbedBlock) => ProsemirrorNode | undefined
      toPrismic(elmt: ProsemirrorNode): EmbedBlock | undefined
    },
  ) {}
}

// Interface for a Prismic Image Block
export class ImageExtension {
  extType: ExtensionType.Node = ExtensionType.Node
  nodeType: NodeType.Image = NodeType.Image

  constructor(
    readonly richTextNodeTypes: ExtendedRichTextNodeType[],
    readonly name: string,
    readonly component: Node,
    readonly converter: {
      fromPrismic(
        schema: Schema,
      ): (elmt: ImageBlock) => ProsemirrorNode | undefined
      toPrismic(elmt: ProsemirrorNode): ImageBlock | undefined
    },
  ) {}
}

// ----- Specific extensions because of custom structure -----
export class HeadingTextExtension extends TextExtension {
  constructor(
    richTextNodeTypes: ExtendedRichTextNodeType[],
    Component: Node,
    readonly levels: readonly Level[],
    Converter: {
      fromPrismic(
        id: string,
        schema: Schema,
        threads?: ThreadData<BaseMetadata>[],
      ): (elmt: TextBlock) => ProsemirrorNode | undefined
      toPrismic(elmt: ProsemirrorNode): TextBlock | undefined
    },
    placeholder?: ExtensionPlaceholder,
  ) {
    super(richTextNodeTypes, "heading", Component, Converter, placeholder)
  }
}

export class ListExtension extends TextExtension {
  // eslint-disable-next-line @typescript-eslint/no-useless-constructor
  constructor(
    richTextNodeTypes: ExtendedRichTextNodeType[],
    name: string,
    Component: Node,
    Converter: {
      fromPrismic(
        id: string,
        schema: Schema,
        threads?: ThreadData<BaseMetadata>[],
      ): (elmt: TextBlock) => ProsemirrorNode | undefined
      toPrismic(elmt: ProsemirrorNode): readonly TextBlock[]
    },
  ) {
    super(richTextNodeTypes, name, Component, Converter)
  }
}

export class TableExtension {
  extType: ExtensionType.Node = ExtensionType.Node
  nodeType: NodeType.Table = NodeType.Table

  constructor(
    readonly richTextNodeTypes: ExtendedRichTextNodeType[],
    readonly name: string,
    readonly component: Node[],
    readonly converter: {
      fromPrismic(
        id: string,
        schema: Schema,
        threads?: ThreadData<BaseMetadata>[],
      ): (elmt: TableBlock) => ProsemirrorNode | undefined
      toPrismic(elmt: ProsemirrorNode): TableBlock | undefined
    },
  ) {}
}

export function isListExtension(
  extension: WidgetExtension,
): extension is ListExtension {
  return extension instanceof ListExtension
}
